import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../primitives/card";
import { Button } from "../../primitives/button";
import { PriorityBadge } from "./PriorityBadge";
import { Zap, Clock, ExternalLink, Lightbulb } from "lucide-react";
import type * as API from "@shared-core/data/api-types";
import { formatDistanceToNow, isPast } from "date-fns";
import { formatShortDateOrDash } from "@shared-core/domain/formatters";
import { useState } from "react";
import { Link } from "react-router";
import { paths } from "@shared-core/routing/routes";
import { RecommendationDetailModal } from "./RecommendationDetailModal";

interface RecommendationCardProps {
  action: API.Opportunity;
  onCreateTicket?: () => void;
  onNotRelevant?: () => void;
  onViewTicket?: (ticketId: string) => void;
  compact?: boolean;
}

export function RecommendationCard({
  action,
  onCreateTicket,
  onNotRelevant,
  onViewTicket,
  compact = false,
}: RecommendationCardProps) {
  const dueDate = action.due_date ? new Date(action.due_date) : null;
  const isPastDue = dueDate
    ? isPast(dueDate) && action.status === "open"
    : false;
  const dueText = dueDate
    ? formatDistanceToNow(dueDate, { addSuffix: true })
    : null;
  const isInactive = action.status !== "open";
  const hasTicket = !!action.converted_to_task_id;
  const customer = action.customer ?? null;

  const [isModalOpen, setIsModalOpen] = useState(false);

  if (compact) {
    return (
      <>
        <div
          onClick={() => setIsModalOpen(true)}
          className={`flex items-start gap-3 p-4 border border-gray-200 rounded-lg bg-card shadow-sm hover:bg-surface-hover transition-colors cursor-pointer ${isInactive ? "opacity-60" : ""}`}
        >
          <Lightbulb className="size-5 text-primary shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2 mb-1">
              <h4 className={`font-medium ${isInactive ? "line-through" : ""}`}>
                {action.title}
              </h4>
              <PriorityBadge priority={action.priority} />
            </div>
            <div
              className={`flex items-center gap-1 text-sm mb-2 ${isPastDue ? "text-destructive" : "text-muted-foreground"}`}
            >
              {customer && (
                <>
                  <Link
                    to={paths.customerDetail({ customerId: customer.id })}
                    onClick={(e) => e.stopPropagation()}
                    className="hover:underline text-[color:var(--color-primary)]"
                  >
                    {customer.name}
                  </Link>
                  <span className="mx-0.5">•</span>
                </>
              )}
              <Clock className="size-3.5" />
              <span>Due {dueText}</span>
            </div>
            <p className="text-sm text-muted-foreground mb-3">
              {action.rationale}
            </p>
            {(hasTicket || action.status === "open") && (
              <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t">
                {hasTicket ? (
                  onViewTicket && (
                    <Button
                      size="sm"
                      onClick={() => onViewTicket(action.converted_to_task_id!)}
                    >
                      <ExternalLink className="size-4" />
                      View Ticket
                    </Button>
                  )
                ) : (
                  <>
                    <div className="flex items-center gap-2">
                      {/* Empty left side for alignment */}
                    </div>
                    <div className="flex items-center gap-2">
                      {onCreateTicket && (
                        <Button size="sm" onClick={onCreateTicket}>
                          <Zap className="size-4" />
                          Convert to Ticket
                        </Button>
                      )}
                      {onNotRelevant && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={onNotRelevant}
                        >
                          Dismiss
                        </Button>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {isModalOpen && (
          <RecommendationDetailModal
            recommendation={action}
            onClose={() => setIsModalOpen(false)}
            onNotRelevant={
              onNotRelevant
                ? () => {
                    setIsModalOpen(false);
                    onNotRelevant();
                  }
                : undefined
            }
            onCreateTicket={
              onCreateTicket
                ? () => {
                    setIsModalOpen(false);
                    onCreateTicket();
                  }
                : undefined
            }
            onViewTicket={
              onViewTicket
                ? (ticketId) => {
                    setIsModalOpen(false);
                    onViewTicket(ticketId);
                  }
                : undefined
            }
          />
        )}
      </>
    );
  }

  return (
    <Card className={`gap-0 ${isInactive ? "opacity-60" : ""}`}>
      <CardHeader className="pb-0">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 flex-1">
            <Lightbulb className="size-5 text-primary shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-1">
                <CardTitle className={isInactive ? "line-through" : ""}>
                  {action.title}
                </CardTitle>
                <PriorityBadge priority={action.priority} />
              </div>
              <div
                className={`text-sm ${isPastDue ? "text-destructive" : "text-muted-foreground"}`}
              >
                {customer && (
                  <>
                    <Link
                      to={paths.customerDetail({ customerId: customer.id })}
                      className="hover:underline text-[color:var(--color-primary)]"
                    >
                      {customer.name}
                    </Link>
                    <span className="mx-1.5">•</span>
                  </>
                )}
                <span className="text-xs text-muted-foreground uppercase tracking-wide">
                  {action.opportunity_family}
                </span>
                <span className="mx-1.5">•</span>
                Due {formatShortDateOrDash(dueDate)}
              </div>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="border-t pt-2">
        <div className="space-y-4">
          <div>
            <h5 className="text-sm font-medium mb-1">Rationale</h5>
            <p className="text-sm text-muted-foreground">{action.rationale}</p>
          </div>
          {action.suggested_message && (
            <div>
              <h5 className="text-sm font-medium mb-1">Suggested Message</h5>
              <p className="text-sm text-muted-foreground italic">
                {action.suggested_message}
              </p>
            </div>
          )}
          {(hasTicket || action.status === "open") && (
            <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t">
              {hasTicket ? (
                onViewTicket && (
                  <Button
                    size="sm"
                    onClick={() => onViewTicket(action.converted_to_task_id!)}
                  >
                    <ExternalLink className="size-4" />
                    View Ticket
                  </Button>
                )
              ) : (
                <>
                  <div className="flex items-center gap-2">
                    {/* Empty left side for alignment */}
                  </div>
                  <div className="flex items-center gap-2">
                    {onCreateTicket && (
                      <Button size="sm" onClick={onCreateTicket}>
                        <Zap className="size-4" />
                        Convert to Ticket
                      </Button>
                    )}
                    {onNotRelevant && (
                      <Button variant="ghost" size="sm" onClick={onNotRelevant}>
                        Dismiss
                      </Button>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
